import { defineStore } from 'pinia';
import { AssistantService } from '@/services/assistant';
import { useIntentsStore } from '@/stores/intents';
import { useHubUiStore } from '@/stores/hubUi';

/**
 * The AI topic fill is made to somewhat support an unknown number of concepts.
 * The backend decides how many to create, but we need some sort of idea in frontend
 * too, since they are done sequentially.
 *
 * This is how many concepts we expect to make.
 */
const assumedConceptCount = 3;
export const letters = ['A', 'B', 'C'];
export const steps = {
  // Figuring out which step we're on
  INIT: 1,
  // Let user configure the source for generating
  SETUP: 2,
  // Waiting for generating to finish at least 1 concept and load its content
  GENERATING: 3,
  // We have at least 1 concept. Content and other concepts may still be loading
  RESULTS: 4,
  // Taking a concept/content and applying it
  TAKING: 5,
  // Any error in any step that made all columns fail
  ERROR: 6,
};

const persistentState = {
  /**
   * @typedef {Object} PersistentTopicAssistantState
   * @property {string[]} subscribed List of intents that we're expecting to finish processing
   */
  /**
   * @type {PersistentTopicAssistantState}
   */
  data: JSON.parse(localStorage.getItem("topicAssistant") as any) || { subscribed: [] },
  /**
   * Tracks count of alerts sent for which intent. Ued for automatic unsubscribing upon expected alert count
   * @type {Record<string, number>} Key is intent, count is number of alerts sent
   */
  alertsSent: {} as Record<string, number>,
  /**
   * Subscribes to an intent:
   * - receive alerts when processing is done/partially done
   * - resume processing if the page is reloaded
   * @param {string} intent
   */
  subscribe(intent: string) {
    console.debug("[persistentState.subscribe] Subscribing to %s", intent);
    if (this.data.subscribed.includes(intent)) return;
    this.data.subscribed.push(intent);
    this.save();
  },
  /**
   * Unsubscribes from intents:
   * - no more resuming this processing
   * - no alerts sent on updates of processing
   * @param {string} intent
   */
  unsubscribe(intent: string) {
    console.debug("[persistentState.unsubscribe] Unsubscribing from %s", intent);
    this.data.subscribed = this.data.subscribed.filter((i: string) => i !== intent);
    this.save();
  },
  /**
   * Marks that an alert was sent for this intent
   * @param {string} intent
   */
  markAlerted(intent: string) {
    if (!this.alertsSent[intent]) this.alertsSent[intent] = 1;
    else this.alertsSent[intent]++;

    console.debug("[persistentState.markAlerted] Marking alert sent for %s. New count: %s", intent, this.alertsSent[intent]);
    if (this.alertsSent[intent] === assumedConceptCount) {
      console.debug("[persistentState.markAlerted] Max alert threshold met, unsubscribing.");
      this.unsubscribe(intent);
    }
  },
  /**
   * Checks whether we should show a UI alert for this intent
   * @param {string} intent
   * @returns {boolean}
   */
  shouldAlert(intent: string): boolean {
    if (!this.data.subscribed.includes(intent)) return false;
    return this.alertsSent[intent] !== assumedConceptCount;
  },
  /**
   * Saves the persistent state data to localStorage
   */
  save() {
    localStorage.setItem("topicAssistant", JSON.stringify(this.data));
  },
  /**
   * Checks to see if there are intents that should be processed
   * @returns {boolean}
   */
  get hasIntentsToProcess(): boolean {
    return !!this.data.subscribed.length;
  },
};
// TODO Remove when done debugging
(window as any).ps = persistentState;

/** Error shown in the topic assistant UI */
interface TopicAssistantError {
  /** The title of the error */
  title?: string | null;
  /** The error message */
  message: string;
}

/** Per-intent working data of the topic assistant */
interface TopicAssistantData {
  /** Whether the user is currently taking a concept */
  isTakingConcept: boolean;
  /** The current task for generating concepts */
  conceptTask?: any;
  /** A list of concept records */
  concepts: any[];
  /** A list of content (boxes/nodes) for each concept */
  contents: any[];
  /** The URL used in setup (cached on submit) */
  url?: string | null;
  /** Optional content used in setup (cached on submit) */
  content?: string | null;
  /** The current step the topic generation is at (one of `steps`) */
  step: number;
  /** Error that will take up the screen because there's nothing to see */
  taskError?: null | TopicAssistantError;
  /** Errors that happened per column (concept or content). Index is column number. */
  columnErrors: Array<null | TopicAssistantError>;
  /** Loose extra fields (e.g. `error` read defensively in init) */
  [key: string]: any;
}

/**
 * @returns {TopicAssistantData}
 */
const makeState = (): TopicAssistantData => ({
  isTakingConcept: false,
  conceptTask: null,
  concepts: [],
  contents: [],
  step: steps.INIT,
  url: '',
  content: '',
  columnErrors: Array(assumedConceptCount).fill(null),
  taskError: null,
});

/**
 * @callback ConceptReadyCallback
 * @param {string} intent The intent that has concepts ready
 * @param {'completed'|'error'} status The status of the concept generation
 * @returns {any|Promise<any>}
 */
interface TopicAssistantState {
  /** The technical name of the current intent you're visiting */
  currentIntent: string;
  /** channelId currently used by the assistant, null = none */
  currentChannelId: string | null;
  /** Key here is the intent technical name, e.g. 'faq_portfolio' */
  perIntent: Record<string, TopicAssistantData>;
  /**
   * If we reloaded the page, this will be false.
   * This means we need to check the persistent state for intents that need to have their processing/checkers resumed.
   */
  _processingResumed: boolean;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/topicAssistant.ts` (namespace
 * `topicAssistant`). In-memory only — no `vuexPersist` option (the module's own
 * `localStorage.getItem('topicAssistant')` bookkeeping via `persistentState` is untouched and kept
 * exactly as-is; that's a hand-rolled persistence mechanism separate from the vuex-persist plugin).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy action `resumeProcessing` referenced the module-level `state`/`getters` consts directly
 *   (bug-for-bug preserved: see inline note at the `_processingResumed`/`_isIntentProcessing`
 *   checks) — replaced with `this` since there is no separate module-level state object anymore.
 * - Legacy action `checkConceptTask` referenced the module-level `state` const directly (documented
 *   in its own JSDoc as "reads the module-level `state` const directly, as in the JS version") —
 *   replaced with `this.perIntent`.
 * - `getIntentName` used `rootGetters['intents/intent'](intent)` → `useIntentsStore().intent(intent)`.
 * - `uiAlert` used `commit('hubUi/addAlert', payload, { root: true })` → `useHubUiStore().addAlert(payload)`.
 *   `hubUi`'s `addAlert` was mutation-only (no action) in the legacy module, so it becomes a
 *   same-named action on `useHubUiStore` — no rename needed on that side.
 * - No getter/action name collisions and no action+mutation same-name pairs beyond the merges noted
 *   inline below.
 */
export const useTopicAssistantStore = defineStore('topicAssistant', {
  state: (): TopicAssistantState => ({
    currentIntent: '',
    currentChannelId: null,
    perIntent: {},
    _processingResumed: false,
    // Uncomment for easier debugging
    // persistentState,
  }),
  getters: {
    /**
     * Check if we're currently loading anything in the current intent
     */
    isLoading(state: TopicAssistantState) {
      if (!(state.currentIntent in state.perIntent)) return false;

      if (state.perIntent[state.currentIntent]?.step === steps.INIT) return true;
      if (state.perIntent[state.currentIntent]?.step === steps.GENERATING) return true;
      if (state.perIntent[state.currentIntent]?.step === steps.SETUP) return false;
      if (state.perIntent[state.currentIntent]?.step === steps.ERROR) return false;
      // Usually if this is true, we're on ERROR step, but check just in case
      if (state.perIntent[state.currentIntent]?.taskError) return false;

      // For RESULTS, it depends.
      // We have to check whether a column had an error, has concept & content, or if we're still waiting for it
      let columnsAccountedFor = 0;
      for (let i = 0; i < assumedConceptCount; i++) {
        const intent = state.perIntent[state.currentIntent];

        if (intent.columnErrors[i]) {
          columnsAccountedFor++;
          continue;
        }

        if (intent.concepts[i] && intent.contents[i]) {
          columnsAccountedFor++;
        }
      }

      // If not all columns are accounted for, we're still loading
      return columnsAccountedFor !== assumedConceptCount;
    },
    /**
     * Returns the task error object if there is any error
     */
    taskError(state: TopicAssistantState) {
      return state.perIntent[state.currentIntent]?.taskError;
    },
    /**
     * Returns the column errors if there are any errors per column
     */
    columnErrors(state: TopicAssistantState) {
      return state.perIntent[state.currentIntent]?.columnErrors;
    },
    /**
     * Returns the step of the current intent you're in
     */
    currentStep(state: TopicAssistantState) {
      return state.perIntent[state.currentIntent]?.step;
    },
    /**
     * Returns the content of the current intent you're in.
     * Array may be empty if it is not yet ready or nto yet loaded.
     */
    contents(state: TopicAssistantState) {
      return state.perIntent[state.currentIntent]?.contents;
    },
    /**
     * Returns the concepts of the current intent you're in.
     * The array may not be entirely filled yet.
     */
    concepts(state: TopicAssistantState) {
      return state.perIntent[state.currentIntent]?.concepts;
    },
    /**
     * Returns the concept task of the current intent you're in.
     */
    conceptTask(state: TopicAssistantState) {
      return state.perIntent[state.currentIntent]?.conceptTask;
    },
    /**
     * Returns any cached setup properties that were previously set in this session
     * @returns {{url: string, content: string}} Strings will be empty or filled
     */
    cachedSetupProperties(state: TopicAssistantState) {
      return {
        url: state.perIntent[state.currentIntent]?.url,
        content: state.perIntent[state.currentIntent]?.content,
      };
    },
    /**
     * Lets you know if at least one concept is ready.
     * Content for said concept may not be loaded yet.
     */
    aConceptIsReady(state: TopicAssistantState) {
      for (let i = 0; i < assumedConceptCount; i++) {
        if (!state.perIntent[state.currentIntent]) continue;
        if (state.perIntent[state.currentIntent].concepts[i]?.conversationId?.state === 'completed') return true;
      }

      return false;
    },
    /**
     * Returns a function to check whether an intent is already being processed
     * @private
     */
    _isIntentProcessing(state: TopicAssistantState) {
      return (intent: string) => !!state.perIntent[intent];
    },
  },
  actions: {
    /**
     * Gets the display name of an intent
     * @param {string} intent
     * @returns {string}
     */
    getIntentName(intent: string) {
      const found = useIntentsStore().intent(intent);
      // Legacy accessed `found.displayName` unguarded under an `any` context; preserve that assumption.
      return found!.displayName || intent;
    },

    /**
     * Checks if 1 or more concepts are ready for a given intent.
     * Identical to `aConceptIsReady`, but you can specify the intent, which may be necessary for background loading.
     * @param {string} intent
     * @returns {boolean}
     */
    hasConceptsReady({ intent }: { intent: string }) {
      for (const concept of this.perIntent[intent].concepts) {
        if (concept.conversationId?.state === 'completed') {
          return true;
        }
      }

      return false;
    },

    /**
     * Starts the generation processing.
     * Will start a process in the background to check for updates every 5 seconds.
     * @param {string} intent The intent to start generating
     * @param {string} botId The bot to use for generating
     * @param {string} url The URL to process
     * @param {?string} [content] Any optional extra information relevant for the generation.
     * @returns {Promise<void>}
     */
    async generateNewConcepts(
      { botId, intent, url, content, channelId }: { botId: string; intent: string; url: string; content?: string; channelId?: string | null }
    ) {
      this.setStep({ step: steps.GENERATING, intent });
      // Clear any errors
      this.clearErrors({ intent });

      // Cache in memory for this session
      this.perIntent[intent].url = url;
      this.perIntent[intent].content = content;
      persistentState.subscribe(intent);

      const result: any = await AssistantService.generateConcepts({
        uniqueBotId: botId,
        intent: intent,
        informations: content,
        websites: [url],
        channel: channelId,
      });

      if (!result.concept) {
        this.uiAlert({ intent, message: 'answerAssistant.error.generateConceptError', type: 'error' });
        this.setStep({ step: steps.ERROR, intent });
        return this.setError({
          intent,
          taskError: true,
          title: 'answerAssistant.error.generateConceptError',
          message: 'answerAssistant.error.generateConceptErrorDesc',
        });
      }

      // Wipe local cache if we managed to start concept generation
      this.setConcepts({ concepts: [], intent });
      this.setConceptTask({ task: null, intent });
      for (let i = 0; i < this.perIntent[intent].contents.length; i++) {
        this.setContent({ index: i, content: null, intent });
      }

      this.setConceptTask({ task: result.concept, intent });
      // Start a background "cron job" that calls the status of the concept task every 5 seconds
      this.checkConceptTask({
        botId,
        taskId: result.concept._id,
        intent,
      });
    },

    /**
     * In case you reloaded the page, you may want to resume processing of some intents,
     * or at least know how they ended up if you comer back at a later point.
     *
     * This method can be called, and it will check if there's anything to process,
     * and if so, will do that.
     *
     * If called in the same scope as init():
     * - ensureExit should be called first, this ensures the record exists in memory
     * - init should be the one to start the processing
     * - resumeProcessing called last, and see that it already exists in memory, so it skips this intent
     * @param {string} botId The ID of the bot to use
     */
    resumeProcessing({ botId }: { botId: string }) {
      console.debug("[resumeProcessing] Resuming processing of intents...");
      if (!persistentState.hasIntentsToProcess || this._processingResumed) {
        console.debug("[resumeProcessing] Nothing to resume.");
        return;
      }

      for (const intentId of persistentState.data.subscribed) {
        if (this._isIntentProcessing(intentId)) {
          console.debug(`[resumeProcessing] Skipping ${intentId} as it is already being processed.`);
          continue;
        }
        this.ensureExists({ intent: intentId });
        console.debug(`[resumeProcessing] Resuming processing of ${intentId}...`);
        this._startCheckingIntent({ intent: intentId, botId });
      }

      console.debug("[resumeProcessing] Done resuming processing. Marking flag as true");
      this._setProcessingResumed({ isResumed: true });
    },

    /**
     * Initializes the page by checking if there are any concepts already generated.
     * This is triggered any time you un-minimize it as well.
     * @param {string} intent The technical name of the current intent, e.g. 'faq_portfolio'
     * @param {string} botId The ID of the bot to use
     * @param {string} [channelId] The ID of the channel to use
     */
    async init({ intent, botId, channelId }: { intent: string; botId: string; channelId?: string | null }) {
      if (channelId !== undefined) this.setCurrentAssistantChannelId(channelId);
      console.debug("[init] Initializing %s in ch %s...", intent, channelId);
      // The editor is reused when only the route `:name` changes, and not every caller
      // guarantees a record for the new intent first (MOIN-HUB-2B). Without a name there is
      // nothing to initialise.
      if (!intent) return;
      this.ensureExists({ intent });
      // If you minimize, then go open it again...
      //... check for error
      if (this.perIntent[intent].error?.message) {
        return this.setStep({ step: steps.ERROR, intent });
      }
      // ... check for concepts ready
      if (await this.hasConceptsReady({ intent })) {
        return this.setStep({ step: steps.RESULTS, intent });
      }
      // ... we already started processing, we do not need to run init
      if (this.perIntent[intent].step !== steps.INIT) {
        return;
      }

      this.setConcepts({ concepts: [], intent });

      return this._startCheckingIntent({ intent, botId });
    },

    /**
     * Internal: A part of the init() method, but split up so that the
     * `resumeProcessing` method can also re-use the parts that matter for it
     * @param {string} intent The technical name of the current intent, e.g. 'faq_portfolio'
     * @param {string} botId The ID of the bot to use
     * @private
     */
    async _startCheckingIntent({ intent, botId }: { intent: string; botId: string }) {
      console.debug("[_startCheckingIntent] Starting processing of %s", intent);

      const result: any = await AssistantService.getFullConceptTask({
        intent,
        uniqueBotId: botId,
        channelId: this.currentChannelId,
      });

      // Nothing generated for this intent before
      if (result === null || result.channelId !== this.currentChannelId) {
        return this.setStep({ step: steps.SETUP, intent });
      }

      // No concepts property (should've been at least an empty array)
      if (!result?.concepts) {
        this.uiAlert({ intent, message: 'answerAssistant.error.loadConceptsError', type: 'error' });
        this.setStep({ step: steps.ERROR, intent });
        return this.setError({
          intent,
          taskError: true,
          title: 'answerAssistant.error.loadConceptsError',
          message: 'answerAssistant.error.loadConceptsErrorDesc',
        });
      }

      // If no concepts, but not open/running, skip to error
      if (!result?.concepts.length && !["open", "running"].includes(result.status)) {
        this.uiAlert({ intent, message: 'answerAssistant.error.generateConceptError', type: 'error' });
        this.setStep({ step: steps.ERROR, intent });
        return this.setError({
          intent,
          taskError: true,
          title: 'answerAssistant.error.generateConceptError',
          message: 'answerAssistant.error.generateConceptErrorDesc',
        });
      }

      // If we've come this far, something is generating
      this.setStep({ step: steps.GENERATING, intent });

      // Store whatever state the concepts are in now
      this.setConcepts({ concepts: result.concepts, intent });

      // Start loading the content for each concept, however far they've come
      for (let i = 0; i < this.perIntent[intent].concepts.length; i++) {
        // If was generated before we might be able to skip directly to loading the actual generated content
        const contentReady = this.perIntent[intent]?.concepts?.[i]?.conversationId?.state === 'completed';
        if (contentReady) {
          await this.fetchConceptContent({
            botId,
            intent,
            index: i,
            conversationId: this.perIntent[intent].concepts[i].conversationId._id,
          });

          continue;
        }

        // Start polling, will self-retry every 5 seconds
        await this.startConceptSummaryLoader({
          botId,
          intent,
          index: i,
          conceptId: this.perIntent[intent].concepts[i]._id,
        });
      }
    },

    /**
     * Loads in the actual boxes/nodes for a given concept by index.
     * Self-triggers a retry every 5 seconds until the content is ready.
     * @param {string} conversationId The ID of the conversation which will contain our nodes/boxes
     * @param {number} index Concept index
     * @param {string} botId The ID of the bot to use
     * @param {string} intent The intent to load the concept for
     */
    async fetchConceptContent(
      { index, botId, conversationId, intent }: { index: number; botId: string; conversationId: string; intent: string }
    ) {
      console.debug("[fetchConceptContent] Starting processing of %s", intent);
      const content: any = await AssistantService.getConversation({
        uniqueBotId: botId,
        conversationId: conversationId,
      });

      if (!content.conversation?.content) {
        this.uiAlert({ intent, message: `answerAssistant.error.loadContentError`, type: 'error' });

        console.debug("[fetchConceptContent] No content found, going to error step.");
        return this.setError({
          intent,
          columnIndex: index,
          title: 'answerAssistant.error.loadContentError',
          message: content.error?.message
            ? content.error.message
            : 'answerAssistant.error.loadContentErrorDesc',
        });
      }

      console.debug("[fetchConceptContent] Setting content:", content.conversation.content);
      // Set the content for a given index that is now ready and loaded
      this.setContent({
        index,
        content: content.conversation.content,
        intent,
      });

      if (this.perIntent[intent].step !== steps.ERROR) {
        console.debug("[fetchConceptContent] Changing step to RESULTS");
        this.setStep({ step: steps.RESULTS, intent });
      }

      /**
       * Because i18n cannot be imported and used independently of a Vue component,
       * and passing a bound function through all the necessary methods is a lot of work,
       * we just hard-code the messages for both A, B, and C.
       */
      const successVariant = (index: number) => `answerAssistant.success.conceptReady${letters[index]}`;
      this.uiAlert({
        intent,
        message: successVariant(index),
        type: 'success',
      });
    },

    /**
     * Load a concept summary. Will self-try again if no box data is ready yet.
     * @param {string} botId The ID of the bot to use
     * @param {string} intent The intent to load the concept for
     * @param {string} conceptId The MongoDB ID of the concept
     * @param {number} index The index at which this concept is stored in the `concepts` array
     * @alias getConcept
     */
    async startConceptSummaryLoader(
      { botId, intent, conceptId, index }: { botId: string; intent: string; conceptId: string; index: number }
    ) {
      console.debug("[startConceptSummaryLoader] Starting processing of %s", intent);
      const result: any = await AssistantService.getConcept({
        conceptId,
        uniqueBotId: botId,
      });

      // No record found
      if (!result?.concept) {
        this.uiAlert({ intent, message: 'answerAssistant.error.noConceptFound', type: 'error' });
        return this.setError({
          intent,
          columnIndex: index,
          title: 'answerAssistant.error.noConceptFound',
          message: 'answerAssistant.error.noConceptFoundDesc',
        });
      }

      // Record should exist by now
      const itsBeenTooLong = await this.checkConceptCreationError({ intent, index });
      if (itsBeenTooLong) {
        this.uiAlert({ intent, message: 'answerAssistant.error.generateConceptError', type: 'error' });

        return this.setError({
          intent,
          columnIndex: index,
          title: 'answerAssistant.error.generateConceptError',
          message: 'answerAssistant.error.generateConceptTooLongError',
        });
      }

      // Cache whatever state it is in...
      this.setConcept({ index, intent, concept: result.concept });

      // ... and if it does not have content ready yet, check again in 5 seconds
      if (
        !result.concept?.conversationId
        || result.concept.conversationId?.state !== 'completed'
      ) {
        console.debug("[startConceptSummaryLoader] Retrying again later...");
        return setTimeout(() => {
          console.debug("[startConceptSummaryLoader] Retrying.");
          return this.startConceptSummaryLoader({
            conceptId,
            index,
            intent,
            botId,
          });
        }, 5000);
      }

      // ... else at least 1 concept is ready, so we can move on
      this.setStep({ step: steps.RESULTS, intent });

      console.debug("[startConceptSummaryLoader] Passing on to fetchConceptContent");
      this.fetchConceptContent({
        index,
        botId,
        conversationId: result.concept.conversationId._id,
        intent,
      });
    },

    /**
     * Checks to see if our request for concepts have yielded any results yet
     * @alias checkConceptTask
     * @param {string} intent the specific intent to target if not current
     * @param {string} botId The bot to use for generating
     * @param {string} taskId The ID of the task to check
     */
    async checkConceptTask(
      { taskId, botId, intent }: { taskId: string; botId: string; intent: string }
    ) {
      const result: any = await AssistantService.getConceptTask({
        taskId: taskId,
        uniqueBotId: botId,
      });
      if (result?.task?.status === 'completed') {
        // Get all concepts from AssistantService startConceptSummaryLoader and push them to the concept array
        for (let i = 0; i < result.task.concepts.length; i++) {
          await this.startConceptSummaryLoader({
            conceptId: result.task.concepts[i],
            index: i,
            intent,
            botId,
          });
        }

        return;
      }

      if (result?.task?.status === 'error') {
        this.setConcepts({ concepts: [], intent });
        this.setConceptTask({ task: null, intent });
        this.setStep({ step: steps.ERROR, intent });

        this.uiAlert({ intent, message: 'answerAssistant.error.generateConceptError', type: 'error' });

        return this.setError({
          intent,
          taskError: true,
          title: 'answerAssistant.error.generateConceptError',
          message: 'answerAssistant.error.generateConceptErrorDesc',
        });
      }

      // Overwrite state tracking if it has progressed
      // NOTE: reads `this.perIntent` directly, as in the legacy JS version (which read the
      // module-level `state` const directly instead of the injected `{ state }` context arg).
      if (
        this.perIntent[intent]?.conceptTask?.status === 'open' &&
        result?.task?.status === 'running'
      ) {
        this.setConceptTask({ task: result.task, intent });
      }

      // Check again in 5 seconds
      setTimeout(() => {
        return this.checkConceptTask({ botId, taskId, intent });
      }, 5000);
    },

    /**
     * Wipes the current records and bring you back to the setup stage,
     * so you can fill inn details again and try to generate anew.
     * @param {?string} [intent] Optional: specific intent to target if not current
     * @returns {Promise<number>}
     */
    async restartFromSetup({ intent }: { intent?: string }) {
      // Go back to the setup step
      this.setStep({ step: steps.SETUP, intent });
    },

    /**
     * Check the concept creation error for a given index on demand
     * @param {string} intent
     * @param {number} index
     * @returns {boolean}
     */
    checkConceptCreationError({ intent, index }: { intent: string; index: number }) {
      if (!this.perIntent[intent].concepts || this.perIntent[intent].concepts.length < index) return false;

      // if the createdAt of a concept is more than 3 minutes ago, it is an error
      if (
        this.perIntent[intent]?.concepts?.[index]?.conversationId
        && this.perIntent[intent].concepts[index].conversationId.state !== 'completed'
        && new Date(this.perIntent[intent].concepts[index].conversationId.createdAt).getTime() < Date.now() - 3 * 60 * 1000
      ) {
        return true;
      }

      // If we got a concept record without a conversation/conversationId property after 3 minutes, it is also an error
      if (
        this.perIntent[intent].concepts[index]?.createdAt
        && !this.perIntent[intent]?.concepts?.[index]?.conversationId
        && new Date(this.perIntent[intent].concepts[index].createdAt).getTime() < Date.now() - 3 * 60 * 1000
      ) {
        return true;
      }

      return false;
    },

    /**
     * Handles adding alerts to the UI for when you are not in the /intent/{intent}/edit view
     * @param {string} message The message to display in the alert
     * @param {string} type The type of alert, e.g., 'error', 'warning', 'info'
     * @param {?string} [intent] Optional: specific intent to target if not current
     */
    async uiAlert({ intent, message, type }: { intent?: string; message: string; type: string }) {
      if (!intent) intent = this.currentIntent;

      if (!persistentState.shouldAlert(intent!)) {
        return persistentState.unsubscribe(intent!);
      }

      if (type === "error") persistentState.unsubscribe(intent!);
      else persistentState.markAlerted(intent!);

      // Legacy: `commit('hubUi/addAlert', payload, { root: true })`.
      useHubUiStore().addAlert({
        message: `${message}`,
        // Legacy passed `type: string` straight through without validating against the alert-type union.
        type: type as 'info' | 'error' | 'success' | 'warning',
        to: {
          name: 'intent-view',
          params: {
            view: 'assistant',
            name: intent || this.currentIntent,
          },
        },
      });
    },

    /**
     * Indicates which intent we're currently using.
     * SHOULD BE USED ANY TIME YOU OPEN AN INTENT TO EDIT IT.
     * Converted from legacy mutation `setCurrentAssistantIntent` (no matching legacy action existed).
     * @param {string} intent
     */
    setCurrentAssistantIntent(intent: string) {
      this.currentIntent = intent;
    },
    /**
     * Converted from legacy mutation `setCurrentAssistantChannelId` (no matching legacy action existed).
     */
    setCurrentAssistantChannelId(channelId: string | null) {
      if (!channelId) {
        this.currentChannelId = null;
      }

      // Check if concepts are available and if the channel ID has changed
      if (this.perIntent[this.currentIntent] &&
          this.perIntent[this.currentIntent].concepts &&
          this.perIntent[this.currentIntent].concepts.length > 0) {
          const currentConceptTask = this.perIntent[this.currentIntent].concepts[0];

          if (currentConceptTask.channelId !== channelId) {
              // Channel ID has changed, remove the concepts and contents and set step to INIT
              this.perIntent[this.currentIntent].concepts = [];
              this.perIntent[this.currentIntent].contents = [];
              this.perIntent[this.currentIntent].step = steps.INIT;
          }
      } else if (this.perIntent[this.currentIntent].step === steps.SETUP) {
        this.perIntent[this.currentIntent].step = steps.INIT;
      }

      this.currentChannelId = channelId;
    },
    /**
     * Ensures that a container for the given intent exists
     * Converted from legacy mutation `ensureExists` (no matching legacy action existed).
     * @param {string} intent The intent ot ensure exists
     * @returns {void}
     */
    ensureExists({ intent }: { intent: string }) {
      if (this.perIntent[intent]) return;
      this.perIntent = { ...this.perIntent, [intent]: makeState() };
    },
    /**
     * Overwrites the concepts
     * Converted from legacy mutation `setConcepts` (no matching legacy action existed).
     * @param {Array<Object>} concepts
     * @param {?string} [intent] Optional: specific intent to target if not current
     */
    setConcepts({ concepts, intent }: { concepts: any[]; intent?: string }) {
      const target = intent || this.currentIntent;
      this.perIntent = { ...this.perIntent, [target]: { ...this.perIntent[target], concepts } };
    },
    /**
     * Sets a concept at a given indes
     * Converted from legacy mutation `setConcept` (no matching legacy action existed).
     * @param {Object} concept
     * @param {number} index
     * @param {?string} [intent] Optional: specific intent to target if not current
     */
    setConcept({ index, concept, intent }: { index: number; concept: any; intent?: string }) {
      const target = intent || this.currentIntent;
      const current = this.perIntent[target].concepts.slice();
      current[index] = concept;
      this.perIntent = { ...this.perIntent, [target]: { ...this.perIntent[target], concepts: current } };
    },
    /**
     * Sets the content for a given item index
     * Converted from legacy mutation `setContent` (no matching legacy action existed).
     * @param {number} index
     * @param {Object} content
     * @param {?string} [intent] Optional: specific intent to target if not current
     */
    setContent({ index, content, intent }: { index: number; content: any; intent?: string }) {
      const target = intent || this.currentIntent;
      const current = this.perIntent[target].contents.slice();
      current[index] = content;
      this.perIntent = { ...this.perIntent, [target]: { ...this.perIntent[target], contents: current } };
    },
    /**
     * Sets which step you are at
     * Converted from legacy mutation `setStep` (no matching legacy action existed).
     * @param {number} step
     * @param {?string} [intent] Optional: specific intent to target if not current
     */
    setStep({ step, intent }: { step: number; intent?: string }) {
      const target = intent || this.currentIntent;
      this.perIntent = { ...this.perIntent, [target]: { ...this.perIntent[target], step } };
    },
    /**
     * Sets the task that is created for generating content
     * Converted from legacy mutation `setConceptTask` (no matching legacy action existed).
     * @param {Object} task The task object
     * @param {?string} [intent] Optional: specific intent to target if not current
     */
    setConceptTask({ task, intent }: { task: any; intent?: string }) {
      const target = intent || this.currentIntent;
      this.perIntent = { ...this.perIntent, [target]: { ...this.perIntent[target], conceptTask: task } };
    },
    /**
     * Sets the active error
     * Converted from legacy mutation `setError` (no matching legacy action existed).
     * @param {?string} [title] Display title for the error. Default is up to the component.
     * @param {string} message
     * @param {?boolean} [taskError] If it was a task error, resulting in all content failing. Must be set if not columnIndex.
     * @param {?number} [columnIndex] The index of the column this error occurred on. Must be set if not taskError.
     * @param {?string} [intent] Optional: specific intent to target if not current
     */
    setError({ intent, columnIndex, title, message, taskError }: { intent?: string; columnIndex?: number | null; title?: string; message: string; taskError?: boolean }) {
      const target = intent || this.currentIntent;
      if (taskError) {
        this.perIntent = { ...this.perIntent, [target]: { ...this.perIntent[target], taskError: { title, message } } };
        return;
      }

      if (columnIndex != null) {
        const cols = this.perIntent[target].columnErrors.slice();
        cols[columnIndex] = { title, message };
        this.perIntent = { ...this.perIntent, [target]: { ...this.perIntent[target], columnErrors: cols } };
        return;
      }

      // clear errors
      this.perIntent = { ...this.perIntent, [target]: { ...this.perIntent[target], taskError: null, columnErrors: Array(assumedConceptCount).fill(null) } };
    },
    /**
     * Clears all errors: taskErrors and every column error
     * Converted from legacy mutation `clearErrors` (no matching legacy action existed).
     * @param {?string} [intent] Optional: specific intent to target if not current
     */
    clearErrors({ intent }: { intent?: string }) {
      const target = intent || this.currentIntent;
      this.perIntent = { ...this.perIntent, [target]: { ...this.perIntent[target], taskError: null, columnErrors: Array(assumedConceptCount).fill(null) } };
    },
    /**
     * Sets the flag indicating if processing of intents has resumed
     * Converted from legacy mutation `_setProcessingResumed` (no matching legacy action existed).
     * @param {boolean} isResumed
     * @private
     */
    _setProcessingResumed({ isResumed }: { isResumed: boolean }) {
      this._processingResumed = isResumed;
    },
  },
});
